<script>
import { mapState, mapActions } from 'vuex';
import { useAlert } from 'dashboard/composables';
import LoadingState from '../../../../../components/widgets/LoadingState.vue';

export default {
  name: 'Processing',
  components: {
    LoadingState,
  },
  computed: {
    ...mapState('knowledge', [
      'uploadedFile',
      'datasetId',
      'crawlResult',
      'knowledgeEngine',
    ]),
    hasCrawlResult() {
      return !!(
        this.crawlResult &&
        this.crawlResult.data &&
        this.crawlResult.data.length
      );
    },
    isChunkOverlapInvalid() {
      return (
        this.splitMethod === 'chunk' &&
        this.chunkWordSize !== null &&
        this.chunkWordSize !== '' &&
        this.chunkOverlapWordSize !== null &&
        this.chunkOverlapWordSize !== '' &&
        Number(this.chunkOverlapWordSize) >= Number(this.chunkWordSize)
      );
    },
    isDelimiterInvalid() {
      return (
        this.splitMethod === 'delimiter' &&
        !!this.delimiter &&
        this.delimiter.length > 10
      );
    },
  },
  data() {
    return {
      indexingTechnique: 'high_quality',
      isLoading: false,
      graphEnabled: true,
      imagesEnabled: true,
      // Chunk-size/overlap and delimiter are mutually exclusive splitting
      // strategies — only the selected method's value is ever sent.
      splitMethod: 'chunk',
      chunkWordSize: 900,
      chunkOverlapWordSize: 75,
      // Literal escape text, not an actual newline byte — a raw newline is
      // invisible/stripped in a single-line text input. Resolved back to a
      // real newline in the store before it's sent (knowledge.js).
      delimiter: '\\n',
    };
  },
  mounted() {
    if (!this.uploadedFile && !this.hasCrawlResult) {
      this.$router.push({ name: 'add_knowledge_upload' });
    }
    // Push the suggested defaults to the store now, otherwise they'd only
    // apply once the user touches a field or switches split method.
    this.syncAdvancedUploadOptions();
  },
  methods: {
    ...mapActions('knowledge', [
      'convertToFile',
      'createKnowledgeBase',
      'uploadDocument',
      'updateKnowledgeGraphEnabled',
      'updateKnowledgeImagesEnabled',
      'updateAdvancedUploadOptions',
    ]),
    setSplitMethod(method) {
      this.splitMethod = method;
      // Clear the other method's fields so only one is ever sent.
      if (method === 'chunk') {
        this.delimiter = null;
        if (this.chunkWordSize === null) {
          this.chunkWordSize = 900;
        }
        if (this.chunkOverlapWordSize === null) {
          this.chunkOverlapWordSize = 75;
        }
      } else {
        this.chunkWordSize = null;
        this.chunkOverlapWordSize = null;
        if (this.delimiter === null) {
          this.delimiter = '\\n';
        }
      }
      this.syncAdvancedUploadOptions();
    },
    syncAdvancedUploadOptions() {
      // Gate strictly on splitMethod, not just on whichever fields happen to
      // be non-null — the two methods' fields default independently and
      // aren't guaranteed to be cleared, so this is the one place that must
      // guarantee only one method's value is ever sent.
      this.updateAdvancedUploadOptions({
        chunkWordSize: this.splitMethod === 'chunk' ? this.chunkWordSize : null,
        chunkOverlapWordSize:
          this.splitMethod === 'chunk' ? this.chunkOverlapWordSize : null,
        delimiter: this.splitMethod === 'delimiter' ? this.delimiter : null,
      });
    },
    async uploadFile() {
      this.isLoading = true;
      try {
        if (this.uploadedFile) {
          await this.createKnowledgeBase();
          await this.uploadDocument();
        } else if (this.hasCrawlResult) {
          await this.createKnowledgeBase();
          await this.convertToFile();
          await this.uploadDocument();
        } else {
          throw new Error(this.$t('KNOWLEDGE.PROCESSING.NO_FILE_ERROR'));
        }
        this.$store.dispatch(
          'onboarding/completeLocalTask',
          'create_knowledge_base'
        );
        this.$router.push({ name: 'add_knowledge_finishing' });
      } catch (err) {
        useAlert(err);
        // useAlert(this.$t('KNOWLEDGE.PROCESSING.ERROR_MESSAGE'))
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>

<template>
  <div class="p-2 md:p-8 flex flex-1 h-full gap-3 flex-col m-0 overflow-auto">
    <!-- Modals -->
    <div
      v-if="isLoading"
      class="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50"
    >
      <div
        class="bg-white dark:bg-slate-800 p-6 rounded-xl shadow-xl max-w-md w-full mx-4"
      >
        <!-- Content -->
        <div class="flex items-start gap-4">
          <LoadingState class="mt-1" />
          <p class="text-sm text-slate-700 dark:text-slate-200 leading-relaxed">
            {{ $t('KNOWLEDGE.PROCESSING.UPLOADING_MESSAGE') }}<br />
            {{ $t('KNOWLEDGE.PROCESSING.UPLOADING_DESCRIPTION') }}
          </p>
        </div>

        <!-- Buttons -->
        <div class="mt-6 grid grid-cols-2 gap-3">
          <router-link :to="{ name: 'knowledge' }">
            <button
              class="w-full py-2 text-white font-semibold rounded-lg transition bg-woot-600 hover:bg-woot-700 focus:ring-2 focus:ring-woot-400 focus:outline-none"
            >
              {{ $t('KNOWLEDGE.PROCESSING.BACK_TO_LISTING') }}
            </button>
          </router-link>

          <router-link v-if="datasetId" :to="`../view/${datasetId}/document`">
            <button
              class="w-full py-2 text-white font-semibold rounded-lg transition bg-woot-600 hover:bg-woot-700 focus:ring-2 focus:ring-woot-400 focus:outline-none"
            >
              {{ $t('KNOWLEDGE.PROCESSING.VIEW_DOCUMENT') }}
            </button>
          </router-link>

          <button
            v-if="!datasetId"
            disabled
            class="w-full py-2 text-white font-semibold rounded-lg transition bg-slate-400 cursor-not-allowed"
          >
            {{ $t('KNOWLEDGE.PROCESSING.VIEW_DOCUMENT') }}
          </button>
        </div>
      </div>
    </div>

    <div class="flex justify-between">
      <h1
        class="flex items-center mb-0 text-2xl font-medium text-slate-900 dark:text-slate-100"
      >
        {{ $t('KNOWLEDGE.PROCESSING.TITLE') }}
      </h1>
      <div class="flex gap-2">
        <router-link
          to="upload"
          class="inline-block bg-woot-500 text-slate-100 px-4 py-2 rounded-md hover:bg-woot-700"
        >
          {{ $t('KNOWLEDGE.PROCESSING.PREVIOUS') }}
        </router-link>
        <button
          v-if="!isLoading"
          :disabled="isChunkOverlapInvalid || isDelimiterInvalid"
          class="inline-block bg-woot-500 text-slate-100 px-4 py-2 rounded-md hover:bg-woot-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
          data-testid="processing_next_button"
          data-guide-target="knowledge-processing-next"
          @click="uploadFile"
        >
          {{ $t('KNOWLEDGE.PROCESSING.NEXT') }}
        </button>
        <button
          v-else
          class="inline-block bg-woot-500 text-slate-100 px-4 py-2 rounded-md hover:bg-woot-700"
        >
          <span class="animate-pulse">{{
            $t('KNOWLEDGE.PROCESSING.LOADING')
          }}</span>
        </button>
      </div>
    </div>

    <!-- lightrag only, ignored by the server for dify. Applies to every
           upload method (file/gdrive/website), which land here regardless
           of which one was used in step 1. -->
    <div v-if="knowledgeEngine === 'lightrag'" class="my-8">
      <div
        class="p-4 border border-slate-200 dark:border-slate-700 rounded-md space-y-4"
      >
        <label class="flex items-start gap-2 cursor-pointer">
          <input
            v-model="graphEnabled"
            type="checkbox"
            class="mt-1"
            @change="updateKnowledgeGraphEnabled(graphEnabled)"
          />
          <span>
            <span class="block font-medium text-sm">{{
              $t('KNOWLEDGE.ADVANCED_UPLOAD_OPTIONS.GRAPH_ENABLED_LABEL')
            }}</span>
            <span class="block text-xs text-slate-500 dark:text-slate-400">{{
              $t('KNOWLEDGE.ADVANCED_UPLOAD_OPTIONS.GRAPH_ENABLED_DESCRIPTION')
            }}</span>
            <span
              class="block text-xs font-medium text-amber-600 dark:text-amber-400"
            >
              {{ $t('KNOWLEDGE.ADVANCED_UPLOAD_OPTIONS.GRAPH_ENABLED_COST') }}
            </span>
          </span>
        </label>

        <label class="flex items-start gap-2 cursor-pointer">
          <input
            v-model="imagesEnabled"
            type="checkbox"
            class="mt-1"
            @change="updateKnowledgeImagesEnabled(imagesEnabled)"
          />
          <span>
            <span class="block font-medium text-sm">{{
              $t('KNOWLEDGE.ADVANCED_UPLOAD_OPTIONS.IMAGES_ENABLED_LABEL')
            }}</span>
            <span class="block text-xs text-slate-500 dark:text-slate-400">{{
              $t('KNOWLEDGE.ADVANCED_UPLOAD_OPTIONS.IMAGES_ENABLED_DESCRIPTION')
            }}</span>
            <span
              class="block text-xs font-medium text-amber-600 dark:text-amber-400"
            >
              {{ $t('KNOWLEDGE.ADVANCED_UPLOAD_OPTIONS.IMAGES_ENABLED_COST') }}
            </span>
          </span>
        </label>

        <div>
          <label class="block text-sm font-medium mb-1">{{
            $t('KNOWLEDGE.ADVANCED_UPLOAD_OPTIONS.SPLIT_METHOD_LABEL')
          }}</label>
          <div class="flex gap-4">
            <label class="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="splitMethod"
                value="chunk"
                :checked="splitMethod === 'chunk'"
                @change="setSplitMethod('chunk')"
              />
              {{
                $t('KNOWLEDGE.ADVANCED_UPLOAD_OPTIONS.SPLIT_METHOD_CHUNK_LABEL')
              }}
            </label>
            <label class="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="splitMethod"
                value="delimiter"
                :checked="splitMethod === 'delimiter'"
                @change="setSplitMethod('delimiter')"
              />
              {{
                $t(
                  'KNOWLEDGE.ADVANCED_UPLOAD_OPTIONS.SPLIT_METHOD_DELIMITER_LABEL'
                )
              }}
            </label>
          </div>
        </div>

        <div
          v-if="splitMethod === 'chunk'"
          class="grid grid-cols-1 sm:grid-cols-2 gap-4"
        >
          <div>
            <label class="block text-sm font-medium mb-1">{{
              $t('KNOWLEDGE.ADVANCED_UPLOAD_OPTIONS.CHUNK_WORD_SIZE_LABEL')
            }}</label>
            <input
              v-model="chunkWordSize"
              type="number"
              min="75"
              max="6000"
              class="w-full"
              @change="syncAdvancedUploadOptions"
            />
            <p class="text-xs text-slate-500 dark:text-slate-400 mt-1">
              {{ $t('KNOWLEDGE.ADVANCED_UPLOAD_OPTIONS.CHUNK_WORD_SIZE_HINT') }}
            </p>
          </div>
          <div>
            <label class="block text-sm font-medium mb-1">{{
              $t('KNOWLEDGE.ADVANCED_UPLOAD_OPTIONS.CHUNK_OVERLAP_LABEL')
            }}</label>
            <input
              v-model="chunkOverlapWordSize"
              type="number"
              min="0"
              max="1500"
              class="w-full"
              @change="syncAdvancedUploadOptions"
            />
            <p class="text-xs text-slate-500 dark:text-slate-400 mt-1">
              {{ $t('KNOWLEDGE.ADVANCED_UPLOAD_OPTIONS.CHUNK_OVERLAP_HINT') }}
            </p>
            <p v-if="isChunkOverlapInvalid" class="text-xs text-red-500 mt-1">
              {{
                $t(
                  'KNOWLEDGE.ADVANCED_UPLOAD_OPTIONS.CHUNK_OVERLAP_VALIDATION_ERROR'
                )
              }}
            </p>
          </div>
        </div>

        <div v-else>
          <label class="block text-sm font-medium mb-1">{{
            $t('KNOWLEDGE.ADVANCED_UPLOAD_OPTIONS.DELIMITER_LABEL')
          }}</label>
          <input
            v-model="delimiter"
            type="text"
            class="w-32"
            @change="syncAdvancedUploadOptions"
          />
          <p class="text-xs text-slate-500 dark:text-slate-400 mt-1">
            {{ $t('KNOWLEDGE.ADVANCED_UPLOAD_OPTIONS.DELIMITER_HINT') }}
          </p>
          <p v-if="isDelimiterInvalid" class="text-xs text-red-500 mt-1">
            {{
              $t('KNOWLEDGE.ADVANCED_UPLOAD_OPTIONS.DELIMITER_VALIDATION_ERROR')
            }}
          </p>
        </div>
      </div>
    </div>

    <!-- <br>

          <label 
            class="flex flex-col border-2 p-6 rounded-md cursor-pointer hover:bg-slate-300 dark:hover:bg-slate-700"
            @click="setChuckSettings('custom')"
            :class="{'border-woot-500' : chunckSettings === 'custom', 'border-slate-200' : chunckSettings !== 'custom' }"
          >  
            <div class="flex justify-between">
              <div>
                <h1>Custom</h1>
                <p>Customize chunks rules, chunks length, and preprocessing rules, etc.</p>
              </div>
              <input name="test" value="2" type="radio">
            </div>

            <div v-if="chunckSettings === 'custom'" class="flex flex-col">
              <hr>
              <label>Delimiter</label>
              <input type="text" class="rounded-md" />
              <br>
              <label>Maximum Chunck Length</label>
              <input type="number" class="rounded-md" />
              <br>
              <label>Chunck Overlap</label>
              <input type="number" class="rounded-md" />
              <br>
              <label>Text Preprocessing Rules</label>
              <br>
              <span class="flex items-center gap-2">
                <label>Replace consecutive spaces, newlines and tabs</label>
                <input type="checkbox" name="rules" value="Replace consecutive spaces, newlines and tabs" />
              </span>
              <span class="flex items-center gap-2">
                <label>Delete all URLs and email addresses</label>
                <input type="checkbox" name="rules" value="Delete all URLs and email addresses" />
              </span>
              <br>
              <span class="flex gap-4 items-center">
                <button class="border-2 border-slate-100 px-4 py-2 rounded-md hover:bg-slate-100 hover:text-black">Confirm and review</button>
                <button class="border-2 border-slate-100 px-4 py-2 rounded-md hover:bg-slate-100 hover:text-black">Reset</button>
              </span>
            </div>
          </label> -->

    <!-- <IndexMode /> -->

    <!-- <div class="my-8 flex p-6 gap-2 rounded-md">
        <div>
          <p>Preprocess documents</p>
          <p class="text-xl">{{ uploadedFile.name.slice(0, 18) + '...' }}</p>
        </div>
        <div class="border-l-2 pl-4 border-white">
          <p>Estimated chuncks</p>
          <p class="text-sm">Calculating...</p>
        </div>
      </div> -->

    <div v-if="crawlResult && crawlResult.data && crawlResult.data.length">
      <div
        v-for="(result, index) in crawlResult.data"
        :key="index"
        class="border-2 rounded-md mb-4 p-4"
      >
        <h1>{{ result.metadata.title }}</h1>
        <p>{{ result.metadata.description }}</p>
        <hr />
        <p class="line-clamp-3">{{ result.markdown }}</p>
      </div>
    </div>
  </div>
</template>
